import type { QueryClient } from '@tanstack/react-query';
import { onlineManager } from '@tanstack/react-query';

import { resetNetworkState } from '../../api/network/networkState';

export const RECONNECT_REFETCH_DEBOUNCE_MS = 1000;

const registeredClients = new Set<QueryClient>();

let reconnectDebounceId: ReturnType<typeof setTimeout> | null = null;
let wasOfflineForReconnect = false;
let reconnectRefetchInProgress = false;
let listenerInstalled = false;

function clearReconnectListener(): void {
  onlineManager.setEventListener(() => undefined);
  onlineManager.setOnline(true);
  listenerInstalled = false;
}

/**
 * Runs a single active stale refetch for all registered query clients.
 * Cancels in-flight fetches first so paused offline retries do not resume
 * alongside fresh reconnect refetches.
 */
async function refetchRegisteredClientsOnce(): Promise<void> {
  if (reconnectRefetchInProgress || registeredClients.size === 0) {
    return;
  }

  reconnectRefetchInProgress = true;

  try {
    await Promise.all(
      [...registeredClients].map((client) =>
        client.cancelQueries({ fetchStatus: 'fetching' }),
      ),
    );
    onlineManager.setOnline(true);
    await Promise.all(
      [...registeredClients].map((client) =>
        client.refetchQueries({ type: 'active', stale: true }),
      ),
    );
  } finally {
    reconnectRefetchInProgress = false;
  }
}

function scheduleReconnectRefetch(): void {
  if (reconnectDebounceId) {
    clearTimeout(reconnectDebounceId);
  }

  reconnectDebounceId = setTimeout(() => {
    reconnectDebounceId = null;

    if (!wasOfflineForReconnect) {
      return;
    }

    wasOfflineForReconnect = false;
    resetNetworkState();
    void refetchRegisteredClientsOnce();
  }, RECONNECT_REFETCH_DEBOUNCE_MS);
}

function installReconnectListener(): void {
  if (globalThis.window === undefined || listenerInstalled) {
    return;
  }

  onlineManager.setEventListener((setOnline) => {
    listenerInstalled = true;

    // Deliberately no `setOnline(navigator.onLine)` seeding here.
    //
    // `onlineManager.isOnline()` reads `navigator.onLine` *live* for as long as
    // nothing has called `setOnline`; the first call pins it to a boolean and it
    // never consults the browser again. Seeding on setup therefore converted a
    // self-correcting live read into a snapshot — and a snapshot taken during a
    // spurious `navigator.onLine === false` (headless browsers and container
    // networking get this wrong) pinned the app offline permanently, because the
    // browser never fires a transition it does not believe happened. That state
    // is invisible from outside: the offline banner reads `navigator.onLine`
    // live and keeps saying "online" while every query sits paused.
    //
    // Real transitions still pin, through the handlers below, which is correct —
    // those reflect something that actually changed.

    const handleOnline = (): void => {
      setOnline(true);

      if (wasOfflineForReconnect) {
        scheduleReconnectRefetch();
        return;
      }

      resetNetworkState();
    };

    const handleOffline = (): void => {
      if (reconnectDebounceId) {
        clearTimeout(reconnectDebounceId);
        reconnectDebounceId = null;
      }
      wasOfflineForReconnect = true;
      setOnline(false);
    };

    globalThis.addEventListener('online', handleOnline);
    globalThis.addEventListener('offline', handleOffline);

    return () => {
      if (reconnectDebounceId) {
        clearTimeout(reconnectDebounceId);
        reconnectDebounceId = null;
      }
      globalThis.removeEventListener('online', handleOnline);
      globalThis.removeEventListener('offline', handleOffline);
      listenerInstalled = false;
    };
  });
}

/**
 * Registers a query client for coordinated reconnect refetching.
 */
export function registerReconnectCoordinator(
  queryClient: QueryClient,
): () => void {
  installReconnectListener();
  registeredClients.add(queryClient);

  return () => {
    registeredClients.delete(queryClient);
    if (registeredClients.size === 0) {
      clearReconnectListener();
    }
  };
}

/**
 * Resets reconnect coordinator state (for tests).
 */
export function resetReconnectCoordinatorState(): void {
  if (reconnectDebounceId) {
    clearTimeout(reconnectDebounceId);
    reconnectDebounceId = null;
  }
  wasOfflineForReconnect = false;
  reconnectRefetchInProgress = false;
  registeredClients.clear();
  clearReconnectListener();
  resetNetworkState();
}
